import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { IoMdDownload } from "react-icons/io";
import { FaEdit, FaSearch } from "react-icons/fa"; // Import FaSearch icon

function AllUsers() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [selectedDay, setSelectedDay] = useState("day1");

  const [usersDay1, setUsersDay1] = useState([]);
  const [usersDay2, setUsersDay2] = useState([]);

  const [loadingDay1, setLoadingDay1] = useState(false);
  const [loadingDay2, setLoadingDay2] = useState(false);

  const [currentPageDay1, setCurrentPageDay1] = useState(1);
  const [currentPageDay2, setCurrentPageDay2] = useState(1);

  const [totalPagesDay1, setTotalPagesDay1] = useState(1);
  const [totalPagesDay2, setTotalPagesDay2] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");

  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");

  const [editSuccessMessage, setEditSuccessMessage] = useState(null);

  useEffect(() => {
    const { message, prevDay, prevPage } = navigate.state || {};

    if (message) {
      setEditSuccessMessage(message);
      const timer = setTimeout(() => {
        setEditSuccessMessage(null);
        navigate.replace({ ...navigate.location, state: undefined });
      }, 3000);
      return () => clearTimeout(timer);
    }

    if (prevDay) {
      setSelectedDay(prevDay);
      if (prevDay === "day1" && prevPage !== currentPageDay1) {
        fetchUsers("day1", prevPage, appliedSearchTerm);
      } else if (prevDay === "day2" && prevPage !== currentPageDay2) {
        fetchUsers("day2", prevPage, appliedSearchTerm);
      } else {
        fetchUsers(prevDay, prevPage, appliedSearchTerm);
      }
    } else {
      fetchUsers("day1", currentPageDay1, appliedSearchTerm);
      fetchUsers("day2", currentPageDay2, appliedSearchTerm);
    }
  }, [currentPageDay1, currentPageDay2, navigate, appliedSearchTerm]); // Dependency changed to appliedSearchTerm

  /*
  const fetchUsers = async (day, page = 1, currentSearchTerm = "") => {
    const storedToken = localStorage.getItem("token");
    const setLoading = day === "day1" ? setLoadingDay1 : setLoadingDay2;
    const setUsers = day === "day1" ? setUsersDay1 : setUsersDay2;
    const setCurrentPage =
      day === "day1" ? setCurrentPageDay1 : setCurrentPageDay2;
    const setTotalPages =
      day === "day1" ? setTotalPagesDay1 : setTotalPagesDay2;

    setLoading(true);
    try {
      const url = new URL(`${process.env.REACT_APP_API_URL}/api/users`);
      url.searchParams.append('day', day);
      url.searchParams.append('page', page);
      if (currentSearchTerm) {
        url.searchParams.append('search', currentSearchTerm);
      }

      const res = await fetch(
        url.toString(),
        {
          headers: { Authorization: `Bearer ${storedToken}` },
        }
      );

      if (res.status === 401) {
        logout();
        navigate("/");
        return;
      }

      const data = await res.json();
      if (res.ok) {
        setUsers(data.users);
        setCurrentPage(data.currentPage);
        setTotalPages(data.totalPages);
      } else {
        alert(data.message || "Failed to fetch users.");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      alert("Error fetching users.");
    } finally {
      setLoading(false);
    }
  };
  */

  const fetchUsers = async (day, page = 1, currentSearchTerm = "") => {
    const storedToken = localStorage.getItem("token");
    const setLoading = day === "day1" ? setLoadingDay1 : setLoadingDay2;
    const setUsers = day === "day1" ? setUsersDay1 : setUsersDay2;
    const setCurrentPage =
      day === "day1" ? setCurrentPageDay1 : setCurrentPageDay2;
    const setTotalPages =
      day === "day1" ? setTotalPagesDay1 : setTotalPagesDay2;

    setLoading(true);
    try {
      let url;
      if (currentSearchTerm) {
        // Use the search API endpoint when there is a search term
        url = new URL(`${process.env.REACT_APP_API_URL}/api/search`);
        url.searchParams.append("name", currentSearchTerm);
      } else {
        // Use the default users API endpoint when no search term is present
        url = new URL(`${process.env.REACT_APP_API_URL}/api/users`);
        url.searchParams.append("day", day);
        url.searchParams.append("page", page);
      }

      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${storedToken}` },
      });

      if (res.status === 401) {
        logout();
        navigate("/");
        return;
      }

      const data = await res.json();
      if (res.ok) {
        // The search API returns a simple array, not a paginated object
        if (currentSearchTerm) {
          setUsers(data); // Set users directly from the returned array
          setCurrentPage(1); // Reset pagination for search results
          setTotalPages(1);
        } else {
          setUsers(data.users);
          setCurrentPage(data.currentPage);
          setTotalPages(data.totalPages);
        }
      } else {
        alert(data.message || "Failed to fetch users.");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      alert("Error fetching users.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async (day) => {
    const storedToken = localStorage.getItem("token");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users?day=${day}&format=xlsx`,
        {
          headers: { Authorization: `Bearer ${storedToken}` },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `registrations_${day}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else if (response.status === 401) {
        logout();
        navigate("/");
      } else {
        const error = await response.json();
        alert(error.message || `Failed to download Excel for ${day}`);
      }
    } catch (err) {
      console.error("Download error:", err);
      alert(`Error downloading Excel for ${day}`);
    }
  };

  const handleEditUser = (userId) => {
    const currentPage =
      selectedDay === "day1" ? currentPageDay1 : currentPageDay2;
    navigate(`/users/edit/${userId}`, {
      state: { prevDay: selectedDay, prevPage: currentPage },
    });
  };

  const formatToIST = (utcString) => {
    if (!utcString) return "N/A";
    const date = new Date(utcString);
    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
    if (selectedDay === "day1") {
      setCurrentPageDay1(1);
    } else {
      setCurrentPageDay2(1);
    }
  };

  const handleResetSearch = () => {
    setSearchTerm("");
    setAppliedSearchTerm("");
    if (selectedDay === "day1") {
      setCurrentPageDay1(1);
      fetchUsers("day1", 1, "");
    } else {
      setCurrentPageDay2(1);
      fetchUsers("day2", 1, "");
    }
  };

  // Allow pressing Enter in the search bar to trigger search
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const renderPagination = (day) => {
    const currentPage = day === "day1" ? currentPageDay1 : currentPageDay2;
    const totalPages = day === "day1" ? totalPagesDay1 : totalPagesDay2;

    if (totalPages <= 1) return null;

    return (
      <div className="d-flex justify-content-center my-3">
        <nav>
          <ul className="pagination">
            <li className={`page-item ${currentPage === 1 && "disabled"}`}>
              <button
                className="page-link"
                onClick={() => {
                  if (currentPage > 1)
                    fetchUsers(day, currentPage - 1, appliedSearchTerm);
                }}
              >
                Previous
              </button>
            </li>
            {[...Array(totalPages)].map((_, index) => (
              <li
                key={index}
                className={`page-item ${
                  currentPage === index + 1 ? "active" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => fetchUsers(day, index + 1, appliedSearchTerm)}
                >
                  {index + 1}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === totalPages && "disabled"
              }`}
            >
              <button
                className="page-link"
                onClick={() => {
                  if (currentPage < totalPages)
                    fetchUsers(day, currentPage + 1, appliedSearchTerm);
                }}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  const renderTable = (users, day, loading) => (
    <div>
      <h4 className="text-center my-3">{day.toUpperCase()}</h4>
      {loading ? (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status"></div>
          <p>Loading {day.toUpperCase()} users...</p>
        </div>
      ) : (
        <>
          {renderPagination(day)}
          <div className="table-responsive">
            <table
              className="table table-striped table-bordered table-hover"
              style={{ backgroundColor: "#2e86c1" }}
            >
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Architect</th>
                  <th>Firm</th>
                  <th>Designation</th>
                  <th>Quote</th>
                  <th>Contact</th>
                  <th>Website</th>
                  <th>Personal Insta</th>
                  <th>Firm Insta</th>
                  <th>Personal LinkedIn</th>
                  <th>Firm LinkedIn</th>
                  <th>2025 Projects</th>
                  <th>Pickup Location</th>
                  <th>Pickup Time</th>
                  <th>Drop Location</th>
                  <th>Evening Drop</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        {user.arc_image ? (
                          <img
                            src={user.arc_image}
                            alt="User"
                            className="img-thumbnail"
                            style={{
                              width: "50px",
                              height: "50px",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>{user.arc_name || "N/A"}</td>
                      <td>{user.firm_name || "N/A"}</td>
                      <td>{user.designation || "N/A"}</td>
                      <td>{user.arc_quote || "N/A"}</td>
                      <td>{user.contact_number || "N/A"}</td>
                      <td>
                        {user.firm_website ? (
                          <a
                            href={
                              user.firm_website.startsWith("http")
                                ? user.firm_website
                                : `https://${user.firm_website}`
                            }
                            target="_blank"
                            rel="noreferrer"
                          >
                            {user.firm_website}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        {user.personal_insta ? (
                          <a
                            href={user.personal_insta}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Link
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        {user.firm_insta ? (
                          <a
                            href={user.firm_insta}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Link
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        {user.personal_linkedin ? (
                          <a
                            href={user.personal_linkedin}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Link
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        {user.firm_linkedin ? (
                          <a
                            href={user.firm_linkedin}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Link
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        {/* Safely parse projects_2025 */}
                        {(() => {
                          try {
                            const projects =
                              typeof user.projects_2025 === "string"
                                ? JSON.parse(user.projects_2025)
                                : user.projects_2025; // If not a string, assume it's already an array or null

                            return Array.isArray(projects)
                              ? projects.join(", ")
                              : projects || "N/A";
                          } catch (e) {
                            console.warn(
                              "Could not parse projects_2025:",
                              user.projects_2025,
                              e
                            );
                            // Fallback to displaying raw value if parsing fails
                            return user.projects_2025 || "N/A";
                          }
                        })()}
                      </td>
                      <td>{user.pick_up_location || "N/A"}</td>
                      <td>{formatToIST(user.pick_up_time)}</td>
                      <td>{user.drop_location || "N/A"}</td>
                      <td>{user.evening_drop_location || "N/A"}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-info text-white"
                          onClick={() => handleEditUser(user.id)}
                          title="Edit User"
                        >
                          <FaEdit />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="17" className="text-center">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="container-fluid py-4">
      <h2 className="text-center mb-4">NAVIG8 VISION DIRECTORY</h2>
      {editSuccessMessage && (
        <div className="alert alert-success text-center mb-3" role="alert">
          {editSuccessMessage}
        </div>
      )}
      <div className="d-flex justify-content-center flex-wrap gap-3 mb-4">
        <button
          className={`btn ${
            selectedDay === "day1" ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => setSelectedDay("day1")}
        >
          DAY 1
        </button>
        <button
          className={`btn ${
            selectedDay === "day2" ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => setSelectedDay("day2")}
        >
          DAY 2
        </button>

        <button
          className="btn btn-success d-flex align-items-center justify-content-center"
          onClick={() => handleDownloadExcel("day1")}
        >
          <IoMdDownload size={20} /> DAY 1
        </button>

        <button
          className="btn btn-success d-flex align-items-center justify-content-center"
          onClick={() => handleDownloadExcel("day2")}
        >
          <IoMdDownload size={20} /> DAY 2
        </button>

        <button onClick={logout} className="btn btn-danger">
          Logout
        </button>
      </div>
      {/* Search Input and Go Button */}
      <div className="row justify-content-center mb-4">
  <div className="col-md-6">
    <div className="input-group">
      <input
        type="text"
        className="form-control"
        placeholder="Search by Architect Name..."
        value={searchTerm}
        onChange={handleSearchInputChange}
        onKeyPress={handleKeyPress}
      />
      <button
        className="btn btn-primary"
        type="button"
        onClick={handleSearchSubmit}
      >
        <FaSearch className="me-2" /> Go
      </button>
      <button
        className="btn btn-secondary"
        type="button"
        onClick={handleResetSearch}
        style={{
          display: appliedSearchTerm ? 'block' : 'none'
        }}
      >
        Reset
      </button>
    </div>
  </div>
</div>
      
      {selectedDay === "day1"
        ? renderTable(usersDay1, "day1", loadingDay1)
        : renderTable(usersDay2, "day2", loadingDay2)}
    </div>
  );
}

export default AllUsers;
